import { useRef, useState } from "react";
import { PaintLineString, PaintMarker, PaintPoint, PaintTypes } from ".";
import { PathOptions } from "leaflet";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Popover,
  Tooltip,
  Typography
} from "@mui/material";
import { listIcons, SelectIcon } from "../icons";
import { Close } from "@mui/icons-material";
import { ChromePicker } from "react-color";
import NumberInput from "../numberInput";

export type TypeLayer = "Polygon" | "LineString" | "Point" | "Marker";

export interface PropsStyle {
  onChange: (style: PaintTypes) => void;
  initialStyle: PaintTypes;
  initialType?: TypeLayer;
}

export const StyleButton: React.FC<PropsStyle> = ({ initialStyle, initialType, onChange }) => {
  const startStyle = useRef(initialStyle);
  const boxSize = "20px";

  const [itemType, setItemType] = useState<TypeLayer>(initialType ?? (
    "icon" in initialStyle ? "Marker" : (
      "radius" in initialStyle ? "Point" : (
        "fillColor" in initialStyle ? "Polygon" : "LineString"
      ))));

  const [itemStyle, setItemStyle] = useState<PaintTypes>(initialStyle);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [anchorTypeLayer, setAnchorTypeLayer] = useState<HTMLElement | null>(null);
  const [anchorFillColor, setAnchorFillColor] = useState<HTMLElement | null>(null);
  const [anchorStrokeColor, setAnchorStrokeColor] = useState<HTMLElement | null>(null);

  const handleStyleFinish = () => {
    if (!itemStyle) return;

    if (onChange) onChange(itemStyle);
    setAnchorEl(null);
  };
  const handleOpenStyle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleChooseType = (type: TypeLayer) => {
    let newStyle: PaintTypes;

    const { ...itemData } = itemStyle;
    if ("weight" in itemData) delete itemData["weight"];
    if ("iconStr" in itemData) delete itemData["iconStr"];
    if ("iconSize" in itemData) delete itemData["iconSize"];

    if (type === "Marker") {
      newStyle = {
        ...itemData,
        iconStr: 'iconStr' in itemStyle ? itemStyle.iconStr : "PinDrop",
        iconSize: "weight" in itemStyle ? itemStyle.weight : (itemStyle as PaintMarker).iconSize,
      };
    } else {
      newStyle = {
        ...itemData,
        weight: "iconSize" in itemStyle ? itemStyle.iconSize : (itemStyle as PathOptions).weight,
      };

      if (type === "Point") {
        (newStyle as PaintPoint).radius = 5;
      }
    }
    setItemStyle(newStyle);
    setItemType(type);
    setAnchorTypeLayer(null);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setItemStyle(startStyle.current);
  };

  let iconButton = <></>;
  if (itemType !== "Marker") {
    iconButton = <Box sx={{
      width: boxSize,
      height: boxSize,
      bgcolor: (itemStyle as PathOptions).fill ? (itemStyle as PathOptions).fillColor : undefined,
      border: `${(itemStyle as PathOptions).weight ?? 1}px ${(itemStyle as PathOptions).dashArray ?? 'solid'} ${itemStyle.color ?? 'black'}`
    }} />;
  } else {
    iconButton = <Icon sx={{
      fontSize: (itemStyle as PaintMarker).iconSize,
      color: (itemStyle as PaintMarker).color,
    }}>
      {listIcons[(itemStyle as PaintMarker).iconStr ?? "PinDrop"]}
    </Icon>;
  }

  return <>
    {/* Icone que abre o menu */}
    <IconButton onClick={handleOpenStyle}>
      {iconButton}
    </IconButton>

    {/* Menu */}
    <Modal
      open={Boolean(anchorEl)}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <>
        <Box sx={{
          display: "flex",
          flexDirection: "column",
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: "50vw",
          height: "75vh",
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: "15px",
          p: "20px",
        }}>
          {/* Botão para fechar */}
          <div style={{
            display: "flex",
            width: "100%",
          }}>
            <Typography
              fontWeight={"bold"}
              fontSize={18}
            >
              Estilo
            </Typography>
            <div style={{ flex: 1 }} />
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </div>
          <Divider />
          <div style={{ height: "15px" }} />

          <div style={{
            flex: 1,
            overflowY: "auto",
          }}>

            {/* Tipo da Layer */}
            <>
              {/* Menu para o tipo do da Layer */}
              <Menu
                id="simple-menu"
                anchorEl={anchorTypeLayer}
                open={Boolean(anchorTypeLayer)}
                onClose={() => setAnchorTypeLayer(null)}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                {(["Polygon", "LineString", "Point", "Marker"] as TypeLayer[]).map((type, i) => {
                  return <MenuItem
                    key={i}
                    onClick={() => handleChooseType(type)}
                  >
                    {type}
                  </MenuItem>;
                })}
              </Menu>

              {/* Botão para o tipo da layer */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>Tipo da Layer:</Typography>
                <div style={{ flex: 1 }} />
                <Button
                  aria-controls={anchorTypeLayer ? 'simple-menu' : undefined}
                  aria-haspopup="true"
                  onClick={(event) => setAnchorTypeLayer(event.currentTarget)}
                  variant="contained"
                >
                  {itemType}
                </Button>
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Divider */}
            <Divider />
            <div style={{ height: "15px" }} />

            {/* Cor de preenchimento de Item */}
            {["Polygon", "LineString", "Point"].includes(itemType) &&
              <>
                {/* Cor de preenchimento */}
                <Popover
                  open={Boolean(anchorFillColor)}
                  anchorEl={anchorFillColor}
                  onClose={() => setAnchorFillColor(null)}
                >
                  <ChromePicker
                    color={(itemStyle as PaintLineString).fillColor}
                    disableAlpha={false}
                    onChange={(color) => setItemStyle({
                      ...itemStyle,
                      fillColor:
                        color.rgb.a ?
                          `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})` :
                          `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`
                    })}
                  />
                </Popover>

                {/* Icone da cor da layer */}
                <div style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <Typography>Cor de preenchimento: </Typography>
                  <div style={{ flex: 1 }} />
                  <Tooltip
                    title="Preenchido"
                  >
                    <Checkbox
                      checked={(itemStyle as PaintLineString).fill ?? false}
                      onChange={(ev, checked) => setItemStyle({ ...itemStyle, fill: checked })}
                    />
                  </Tooltip>
                  <div style={{ width: '15px' }} />
                  {(itemStyle as PathOptions).fill &&
                    <IconButton onClick={(event) => setAnchorFillColor(event.currentTarget)}>
                      <Box sx={{
                        width: boxSize,
                        height: boxSize,
                        bgcolor: (itemStyle as PaintLineString).fillColor,
                        border: `${(itemStyle as PaintLineString).weight ?? 1}px solid ${itemStyle.color ?? '#00000000'}`
                      }} />
                    </IconButton>
                  }
                </div>
                <div style={{ height: "15px" }} />
              </>
            }


            {/* Tamanho do ponto */}
            {itemType === "Point" &&
              <>
                <div style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <Typography>Tamanho do Ponto</Typography>
                  <div style={{ flex: 1 }} />

                  <NumberInput
                    placeholder={"Tamanho do Ponto"}
                    value={(itemStyle as PaintPoint).radius}
                    onChange={(val) => {
                      const size = (val && val < 0) ? 0 : (val ?? undefined);
                      setItemStyle({
                        ...itemStyle,
                        radius: size,
                      });
                    }}
                  />
                </div>
                <div style={{ height: "15px" }} />
              </>
            }

            {/* Divider */}
            <Divider />
            <div style={{ height: "15px" }} />

            {/* Icone no caso do Marker */}
            {itemType === "Marker" &&
              <>
                <div style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                  <Typography>Icone: </Typography>
                  <div style={{ flex: 1 }} />
                  <SelectIcon
                    iconColor={(itemStyle as PaintMarker).color}
                    iconSize={(itemStyle as PaintMarker).iconSize}
                    icon={(itemStyle as PaintMarker).iconStr}
                    onChoose={(icon) => setItemStyle({
                      ...itemStyle,
                      iconStr: icon,
                    })}
                  />
                </div>
                <div style={{ height: "15px" }} />
              </>
            }

            {/* Cor da borda ou preenchimento do Icone no Marker */}
            <>
              <Popover
                open={Boolean(anchorStrokeColor)}
                anchorEl={anchorStrokeColor}
                onClose={() => setAnchorStrokeColor(null)}
              >
                <ChromePicker
                  color={itemStyle.color}

                  disableAlpha={false}
                  onChange={(color) => setItemStyle({
                    ...itemStyle,
                    color:
                      color.rgb.a ?
                        `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})` :
                        `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`
                  })}
                />
              </Popover>

              {/* Texto e Botão */}
              <div style={{
                display: "flex",
                width: "100%",
                justifyContent: "center",
                alignItems: "center",
              }}>
                <Typography>{itemType === "Marker" ? "Cor do Icone" : "Cor da Borda:"}</Typography>
                <div style={{ flex: 1 }} />

                <IconButton onClick={(event) => setAnchorStrokeColor(event.currentTarget)}>
                  {itemType !== "Marker" && <Box sx={{
                    width: boxSize,
                    height: boxSize,
                    border: `${(itemStyle as PathOptions).weight ?? 1}px ${(itemStyle as PathOptions).dashArray ?? 'solid'} ${itemStyle.color ?? '#00000000'}`,
                  }} />}
                  {itemType === "Marker" && <Box sx={{
                    width: boxSize,
                    height: boxSize,
                    bgcolor: itemStyle.color,
                  }} />}
                </IconButton>
              </div>
            </>
            <div style={{ height: "15px" }} />

            {/* Tamanho da Borda ou do Icone no Marker */}
            <div style={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
            }}>
              <Typography>{itemType === "Marker" ? "Tamanho do Icone" : "Largura da Borda"}</Typography>
              <div style={{ flex: 1 }} />

              <NumberInput
                placeholder={itemType === "Marker" ? "Tamanho do Icone" : "Largura da Borda"}
                value={"iconSize" in itemStyle ? itemStyle.iconSize : ((itemStyle as PathOptions).weight ?? 1)}
                onChange={(val) => {
                  if (itemType === "Marker") {
                    const size = (val && val < 0) ? 0 : (val ?? undefined);
                    setItemStyle({
                      ...itemStyle,
                      iconSize: size,
                    });
                  } else {
                    const size = (val && val < 0) ? 0 : (val ?? undefined);
                    setItemStyle({
                      ...itemStyle,
                      weight: size,
                    });
                  }

                }}
              />
            </div>
            <div style={{ height: "15px" }} />

            {/* Divider */}
            <Divider />
            <div style={{ height: "15px" }} />
          </div>

          <div style={{ height: "15px" }} />
          {/* Botão para finalizar */}
          <div style={{
            display: "flex",
            width: "100%",
            justifyContent: "end",
          }}>
            <Button
              variant="contained"
              onClick={handleStyleFinish}
            >Feito</Button>
          </div>
        </Box>
      </>
    </Modal>
  </>;
};